
export enum EmployeeStatus {
  Available = 'available',
  Working = 'working',
  Blocked = 'blocked',
  Review = 'review',
  Completed = 'completed',
  OnHold = 'on_hold'
}

/** Metadados de quem registrou uma ocorrência e quando. */
export interface RecordAuditMeta {
  /** UUID do usuário que registrou (persistido no BD). */
  createdBy?: string;
  /** Nome de exibição do usuário que registrou. */
  createdByUsername?: string;
  /** Momento do registro no formato ISO. */
  createdAt?: string;
}

export interface Absence extends RecordAuditMeta {
  id: string;
  date: string;
  reason: string;
}

/** Registro de atraso do colaborador (mesmo formato de falta). */
export interface Delay extends RecordAuditMeta {
  id: string;
  date: string;
  reason: string;
}

/** Registro de saída antecipada do colaborador (mesmo formato de falta). */
export interface EarlyDeparture extends RecordAuditMeta {
  id: string;
  date: string;
  reason: string;
}

/** Registro de situação ocorrida com o colaborador (histórico no modal do funcionário). */
export interface Situation extends RecordAuditMeta {
  id: string;
  date: string;
  description: string;
}

export interface Employee {
  id: string;
  name: string;
  role: string;
  avatar: string;
  status: EmployeeStatus;
  /** Derivado da data de admissão (não persistido manualmente). */
  probationPeriod?: '45' | '90' | null;
  absences?: Absence[];
  delays?: Delay[];
  earlyDepartures?: EarlyDeparture[];
  situations?: Situation[];
  code?: string;
  hiringDate?: string;
  birthDate?: string;
  whatsapp?: string;
  lastBirthdayMsgYear?: number;
  afastamentoDate?: string;
  costCenterCode?: string;
  costCenterDescription?: string;
}

export interface CostCenter {
  code: string;
  description: string;
}

export interface Column {
  id: string;
  title: string;
  color?: string;
  items: Employee[];
}

/** O que o usuário pode abrir dentro de Configurações (modal lateral). */
export interface SettingsTabPermissions {
  roles: boolean;
  columns: boolean;
  users: boolean;
  messaging: boolean;
  costCenters: boolean;
  postgres: boolean;
  monday: boolean;
  versions: boolean;
  logs: boolean;
}

export interface UserPermissions {
  dashboard: boolean;
  hr: boolean;
  /** Acesso ao modal Configurações (master). */
  settings: boolean;
  absences: boolean;
  /** Pode cadastrar novos colaboradores no quadro. */
  employeeCreate: boolean;
  /** Pode editar dados e mover colaboradores entre setores. */
  employeeEdit: boolean;
  /** Pode excluir colaboradores do quadro. */
  employeeDelete: boolean;
  /** Quando `settings` é true: quais abas do modal estão liberadas. Se ausente no legado, assume-se tudo liberado. */
  settingsTabs: SettingsTabPermissions;
}

export type Company = 'Sanpack' | 'Sanplast' | 'GSan (ADMINISTRATIVO)';

export interface User {
  id: string;
  username: string;
  permissions: UserPermissions;
  allowedCompanies: Company[];
}

export interface LogEntry {
  id: string;
  userId: string;
  username: string;
  timestamp: string;
  action: string;
  details: string;
  /** Empresa à qual o log pertence (quando carregado do banco). */
  companyId?: string;
}

export interface MessagingConfig {
  enabled: boolean;
  baseUrl: string;
  apiKey: string;
  instance: string;
  birthdayEnabled: boolean;
  birthdayTemplate: string;
  birthdaySendHour: number;
  birthdaySendMinute: number;
  generalEnabled: boolean;
  generalTemplate: string;
}

export interface PostgresConfig {
  /** URL base da API Postgres (vazia = mesma origem do site). */
  url: string;
}

export interface MondayIntegrationConfig {
  enabled: boolean;
  boardId: string;
  triggerStatusColumnName: string;
  triggerStatusDoneLabel: string;
  /** Segredo na URL/header do webhook; env MONDAY_WEBHOOK_SECRET tem prioridade. */
  webhookSecret?: string;
  mondayApiToken: string;
  mondayColumnNameTitle: string;
  mondayColumnCompanyTitle: string;
  mondayColumnRoleTitle: string;
  mondayColumnCodeTitle: string;
  mondayColumnBirthDateTitle: string;
  mondayColumnWhatsappTitle: string;
}

export interface MondayWebhookLog {
  ts: string;
  type: string;
  level: 'info' | 'warn' | 'error';
  message: string;
  event?: unknown;
}

/** Textos exibidos no modal do troféu (header); editáveis em Configurações → Versões. */
export interface AppVersionInfo {
  versionLabel: string;
  improvements: string;
  roadmap: string;
}

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'add' | 'delete' | 'info';
  timestamp: string;
  read: boolean;
  /** Origem da notificação — server = fan-out da empresa; local = erros da sessão. */
  source?: 'server' | 'local';
}

export interface FilterState {
  probation45: boolean;
  probation90: boolean;
  hasAbsences: boolean;
  isBirthday: boolean;
}
